import { useEffect, useState } from 'react'
import { type QueueState, replayQueue, subscribeToQueue } from '../api/client.js'

/**
 * The queue, made visible.
 *
 * A silent queue is indistinguishable from a lost edit. If I tick something off
 * while a deploy is swapping containers and the app looks exactly as it does
 * when the tick was saved, I have no way to know the difference — and no reason
 * to check later. So the count is on screen while anything is still waiting.
 */
export function QueueIndicator() {
  const [state, setState] = useState<QueueState>({
    pending: 0,
    failed: 0,
    syncing: false,
    online: true,
    lastSyncedAt: null,
  })

  useEffect(() => subscribeToQueue(setState), [])

  if (state.failed > 0) {
    return (
      <Pill tone="overdue" onClick={() => void replayQueue()} label={`Retry ${state.failed} failed`}>
        <Retry />
        {state.failed} failed
      </Pill>
    )
  }
  if (state.syncing) return <Pill tone="accent">syncing…</Pill>
  if (state.pending > 0) {
    // Tappable: if I can see the count I should be able to make it try again,
    // rather than waiting for the browser to notice the network came back.
    return (
      <Pill tone="soon" onClick={() => void replayQueue()} label={`Retry ${state.pending} queued`}>
        <Retry />
        {state.pending} queued
      </Pill>
    )
  }
  return null
}

/**
 * The glyph that says "this is a button".
 *
 * It used to be a `title` tooltip, which on the device this app is actually
 * used on does not exist — a phone has no hover. The pill said "3 queued" and
 * gave no sign that tapping it would do anything, so the one control that
 * makes a stuck queue move was discoverable only on a laptop.
 */
function Retry() {
  return (
    <svg viewBox="0 0 12 12" className="h-3 w-3 shrink-0" aria-hidden>
      <path
        d="M10 6a4 4 0 1 1-1.2-2.85M10 1v3H7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Pill({
  tone,
  children,
  onClick,
  label,
}: {
  tone: 'accent' | 'soon' | 'overdue' | 'unknown'
  children: React.ReactNode
  onClick?: () => void
  /** Spoken label. The visible text is a count; this says what tapping does. */
  label?: string
}) {
  const colours = {
    accent: 'text-[var(--accent)] bg-[var(--accent-wash)] border-[var(--accent-line)]',
    soon: 'text-[var(--soon)] bg-[var(--soon-wash)] border-[var(--soon)]/30',
    overdue: 'text-[var(--overdue)] bg-[var(--overdue-wash)] border-[var(--overdue)]/30',
    unknown: 'text-[var(--unknown)] bg-[var(--unknown-wash)] border-[var(--unknown)]/30',
  }[tone]

  const Tag = onClick ? 'button' : 'span'
  return (
    <Tag
      {...(onClick ? { onClick, type: 'button' as const, 'aria-label': label } : {})}
      className={`tabular inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] uppercase tracking-[0.08em] ${colours} ${
        onClick ? 'tap cursor-pointer' : ''
      }`}
    >
      {children}
    </Tag>
  )
}
